import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";

// Detects a new deployment by watching the CRA build's asset-manifest hash,
// then offers a one-tap refresh (no reinstall needed).
async function fetchMainHash() {
  try {
    const res = await fetch("/asset-manifest.json", { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    const main = data.files?.["main.js"] || (data.entrypoints && data.entrypoints[0]);
    return main || null;
  } catch {
    return null;
  }
}

export default function UpdatePrompt() {
  const initial = useRef(null);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let stop = false;

    const check = async () => {
      const hash = await fetchMainHash();
      if (stop || !hash) return;
      if (initial.current === null) { initial.current = hash; return; }
      if (hash !== initial.current) setAvailable(true);
    };

    check();
    const interval = setInterval(check, 60000);
    const onVisible = () => { if (document.visibilityState === "visible") {
      check();
      navigator.serviceWorker?.getRegistrations?.().then((rs) => rs.forEach((r) => r.update())).catch(() => {});
    }};
    document.addEventListener("visibilitychange", onVisible);

    return () => { stop = true; clearInterval(interval); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  const refresh = async () => {
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch { /* ignore */ }
    window.location.reload();
  };

  if (!available) return null;

  return (
    <div className="fixed left-3 right-3 bottom-24 md:bottom-6 md:left-1/2 md:right-auto md:-translate-x-1/2 z-[70] glass border border-gold/30 rounded-2xl p-3 flex items-center gap-3 shadow-2xl animate-in fade-in slide-in-from-bottom-4" data-testid="update-prompt">
      <span className="w-9 h-9 rounded-xl gold-gradient text-obsidian flex items-center justify-center shrink-0"><RefreshCw className="w-4 h-4" /></span>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-semibold">New version available</p>
        <p className="text-slate-400 text-xs">Refresh to get the latest drops & features.</p>
      </div>
      <button onClick={refresh} className="gold-gradient text-obsidian font-bold text-sm px-4 py-2 rounded-xl whitespace-nowrap" data-testid="update-refresh-button">Refresh</button>
    </div>
  );
}
